import { VoicemailStatus } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { produce } from 'immer';
import { omit } from 'lodash-es';
import { InfiniteData, useMutation, useQueryClient } from 'react-query';
import { VoicemailApi, VoicemailTypes as VmTypes } from '@frontend/api-voicemails';
import { useTranslation } from '@frontend/i18n';
import { RecentCallRecords } from '@frontend/phone-panels';
import { useAlert } from '@frontend/design-system';
import { VoicemailTableDataRow } from '../components/all-calls/types';
import { queryKeys } from '../query-keys';
import {
  performOptimisticDelete,
  performOptimisticUpdate,
  createReadStatusUpdater,
} from '../utils/voicemail-optimistic-updates';

type UseVoicemailMutationsOptions = {
  selectedLocationIds: string[];
  onDeleteSuccess?: ({ voicemailId, mailboxId }: { voicemailId: string; mailboxId: string }) => void;
  optimisticUpdateProps?:
    | {
        updateType: 'many';
        pageNumber: number;
        pageSize: number;
        queryKey: (string | boolean | number)[];
        fetchNextPage?: (options: any) => Promise<any>;
        onUpdateMutate?: (data?: VoicemailTableDataRow) => void;
        onUpdateError?: (oldData?: VoicemailTableDataRow) => void;
      }
    | {
        updateType: 'one';
        queryKey: (string | boolean | number)[];
        onUpdateMutate?: (data?: VoicemailTableDataRow) => void;
        onUpdateError?: (oldData?: VoicemailTableDataRow) => void;
      };
};

type RecentCallsData = InfiniteData<RecentCallRecords>;

export const useVoicemailMutations = (options: UseVoicemailMutationsOptions) => {
  const { selectedLocationIds, onDeleteSuccess, optimisticUpdateProps } = options;
  const { t } = useTranslation('calls');

  const queryClient = useQueryClient();
  const alert = useAlert();

  const updateRecentCallsVoicemailDeletionStatus = (voicemailId?: string) => {
    if (!voicemailId) return;

    queryClient.setQueriesData<RecentCallsData | undefined>(
      {
        queryKey: [selectedLocationIds, ...queryKeys.base, 'recentCalls'],
        exact: false,
      },
      (oldData) => {
        if (!oldData?.pages) {
          return oldData;
        }

        const updateRecords = (records: RecentCallRecords['data']) =>
          records?.map((record) => {
            const matches = record.channelId === voicemailId || record.voicemailId === voicemailId;

            if (!matches) return record;

            return {
              ...record,
              voicemailStatus: VoicemailStatus.VOICEMAIL_STATUS_DELETE,
            };
          });

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: updateRecords(page.data),
            rows: updateRecords(page.rows),
          })),
        };
      }
    );
  };

  const fullQueryKey = optimisticUpdateProps ? ([selectedLocationIds, ...optimisticUpdateProps.queryKey] as any[]) : [];

  const updateReadStatus = useMutation(
    (payload: VmTypes.StatusPayload) => {
      const req = omit(payload, 'index');
      return VoicemailApi.setVoicemailViewStatus(req);
    },
    {
      onMutate: async ({ voicemailId, status }) => {
        if (optimisticUpdateProps?.updateType === 'many') {
          queryClient.cancelQueries(fullQueryKey);

          if (!voicemailId || !status) return;
          const oldData = queryClient.getQueryData<VoicemailTableDataRow>(fullQueryKey);

          const updateFn = createReadStatusUpdater(status);
          const updatedData = performOptimisticUpdate({
            queryClient,
            fullQueryKey,
            voicemailId,
            pageSize: optimisticUpdateProps.pageSize ?? 25,
            updateFn,
          });

          if (updatedData) {
            queryClient.setQueryData(fullQueryKey, updatedData.data);
            optimisticUpdateProps.onUpdateMutate?.(updatedData.voicemail);
          }

          return { oldData };
        } else if (optimisticUpdateProps?.updateType === 'one') {
          // Handle single update case
          if (!voicemailId || !status) return;

          queryClient.cancelQueries(fullQueryKey);

          const updateFn = createReadStatusUpdater(status);
          const oldData = queryClient.getQueryData<VoicemailTableDataRow>(fullQueryKey);

          const newData = produce(oldData, (draft) => {
            if (draft) {
              updateFn(draft);
            }
          });

          if (newData) {
            queryClient.setQueryData(fullQueryKey, newData);
            optimisticUpdateProps.onUpdateMutate?.(newData);
          }

          return { oldData };
        }

        return;
      },
      onError: (_, _1, ctx) => {
        if (ctx?.oldData) {
          queryClient.setQueryData(fullQueryKey, ctx.oldData);
          optimisticUpdateProps?.onUpdateError?.(ctx.oldData);
        }
      },
    }
  );

  const deleteVoicemail = useMutation((req: VmTypes.DeletePayload) => VoicemailApi.deleteVoicemailMedia(req), {
    onMutate: async ({ index }) => {
      if (optimisticUpdateProps?.updateType === 'many') {
        queryClient.cancelQueries(fullQueryKey);

        const newPageData = await performOptimisticDelete({
          queryClient,
          fullQueryKey,
          pageSize: optimisticUpdateProps.pageSize,
          pageNumber: optimisticUpdateProps.pageNumber,
          index,
          fetchNextPage: optimisticUpdateProps.fetchNextPage,
        });

        if (newPageData) {
          queryClient.setQueryData(fullQueryKey, newPageData);
        }
      }
    },
    onError: () => {
      alert.error(t('voicemailDeletedError'));
    },
    onSuccess: (_, { voicemailId, mailboxId }) => {
      alert.success(t('voicemailDeletedSuccess'));
      if (voicemailId) {
        updateRecentCallsVoicemailDeletionStatus(voicemailId);
        onDeleteSuccess?.({ voicemailId, mailboxId });
      }
    },
  });

  return {
    updateReadStatus: updateReadStatus.mutate,
    deleteVoicemail: deleteVoicemail.mutate,
  };
};
